import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * KpiCard — clickable KPI tile with a headline value, label & optional hint.
 * Consistent across all role-scoped dashboard clusters.
 */
export function KpiCard({ label, value, icon: Icon, tone = "green", onClick, testId, hint }) {
  const toneCls = {
    green: "bg-[#E1F0E6] text-[#0F5E39]",
    gold:  "bg-[#F6EED8] text-[#6E5416]",
    amber: "bg-amber-50 text-amber-700",
    red:   "bg-red-50 text-red-700",
    blue:  "bg-blue-50 text-blue-700",
    slate: "bg-[#F1EDDF] text-[#4A5A50]",
  }[tone] || "bg-[#E1F0E6] text-[#0F5E39]";

  const Wrap = onClick ? "button" : "div";
  return (
    <Wrap
      onClick={onClick}
      className={`text-left w-full bg-white rounded-2xl border border-[#E4DFCF] p-5 transition group ${onClick ? "hover:shadow-[0_12px_32px_rgba(20,38,28,0.08)] hover:-translate-y-[2px] cursor-pointer" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] font-semibold">{label}</div>
          <div className="font-serif text-4xl font-medium mt-2 text-[#C9A24B]">{value}</div>
          {hint && <div className="text-xs text-[#4A5A50] mt-1">{hint}</div>}
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${toneCls}`}>
            <Icon size={18} strokeWidth={1.6}/>
          </div>
        )}
      </div>
      {onClick && (
        <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#0F5E39] font-semibold inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
          View list <ArrowRight size={12}/>
        </div>
      )}
    </Wrap>
  );
}

/**
 * KpiGrid — layout wrapper for KPI clusters.
 */
export function KpiGrid({ children, cols = 4, className = "" }) {
  const colsCls = { 2: "grid-cols-2", 3: "grid-cols-2 md:grid-cols-3", 4: "grid-cols-2 md:grid-cols-4", 5: "grid-cols-2 md:grid-cols-5" }[cols] || "grid-cols-2 md:grid-cols-4";
  return <div className={`grid ${colsCls} gap-4 ${className}`}>{children}</div>;
}
